import {
  deepFlatChildren,
  RgDataSetID,
  RougeDataSets,
  RvDataListSets,
  type RvData,
} from "@project-rouge/rg-core";

// Data + helpers for the Component Library admin screen. The kit of parts one
// level below a Module: walls, floors, doors, windows, beams, columns. Driven
// by RvDataListSets (the authoritative per-dataset element registry) rather
// than by walking modules, so each distinct component appears exactly once.

export type ComponentEntry = {
  rv: RvData;
  /** how many times this modulousId is instantiated across the dataset's apartments */
  usageCount: number;
};

// Tally modulousId occurrences across every apartment in a dataset.
function getUsageMap(id: RgDataSetID): Record<string, number> {
  const usage: Record<string, number> = {};
  for (const apartment of RougeDataSets[id].apartments.all) {
    for (const child of deepFlatChildren(apartment)) {
      if (!child.modulousId) continue;
      usage[child.modulousId] = (usage[child.modulousId] ?? 0) + 1;
    }
  }
  return usage;
}

export function buildComponentCatalog(id: RgDataSetID): ComponentEntry[] {
  const usage = getUsageMap(id);
  return RvDataListSets[id].map((rv) => ({
    rv,
    usageCount: usage[rv.modulousId] ?? 0,
  }));
}

// Group entries by their primary rgType (Wall, Door, Window, …) for sectioning.
// rgType is authoritative in the data; modCategory codes (WPA, DCD, …) are
// shown as sub-labels.
export function groupByRgType(entries: ComponentEntry[]): [string, ComponentEntry[]][] {
  const groups: Record<string, ComponentEntry[]> = {};
  for (const entry of entries) {
    const key = entry.rv.rgTypes[0] ?? "Other";
    (groups[key] ??= []).push(entry);
  }
  // Rough architectural order; anything unlisted falls to the end alphabetically.
  const order = [
    "Wall",
    "Floor",
    "Ceiling",
    "Door",
    "Window",
    "Beam",
    "Column",
    "Roof",
    "Facade",
    "Parapet",
    "Glazing",
  ];
  return Object.entries(groups).sort(([a], [b]) => {
    const ia = order.indexOf(a);
    const ib = order.indexOf(b);
    if (ia === -1 && ib === -1) return a.localeCompare(b);
    if (ia === -1) return 1;
    if (ib === -1) return -1;
    return ia - ib;
  });
}

export const COMPONENT_DATASETS: RgDataSetID[] = [RgDataSetID.uk_01, RgDataSetID.uk_02];
